<template>
  <panel-navigation>

    <!-- ===== SUBSCRIPTION_FEATURE_START ===== -->
    <!--
    <h1 class="text-lg font-semibold mb-4">Billing & Subscription</h1>
    <hr class="border-b border-gray-100 mb-3">

    <div v-if="loading" class="text-center py-8">
      <i class="fa fa-spinner fa-spin text-3xl text-gray-500"></i>
      <p class="mt-2 text-gray-500">Loading billing information...</p>
    </div>

    <div v-else>
      <div class="dashboard-box">
        <v-card class="pa-2" tile>
          <div class="p-3 pt-1">
            <v-tabs v-model="activeTab">
              <v-tab>Subscription</v-tab>
              <v-tab>Invoices</v-tab>
              <v-tab>Payment Method</v-tab>

              <!-- Subscription Tab -->
              <v-tab-item class="pt-5">
                <div class="subscription-section">
                  <!-- Current Plan Card -->
                  <div v-if="subscription" class="current-plan-card mb-6 p-6 bg-gray-50 rounded-lg">
                    <h3 class="text-lg font-semibold mb-4">Current Subscription</h3>
                    <div class="flex flex-wrap">
                      <div class="w-full md:w-1/2 mb-4">
                        <p class="text-sm text-gray-600 mb-1">Plan Type</p>
                        <p class="text-xl font-semibold capitalize">{{ subscription.plan_type }} Plan</p>
                      </div>
                      <div class="w-full md:w-1/2 mb-4">
                        <p class="text-sm text-gray-600 mb-1">Status</p>
                        <span :class="getStatusClass(subscription.status)" class="px-3 py-1 rounded-full text-sm font-semibold">
                          {{ subscription.status }}
                        </span>
                      </div>
                      <div class="w-full md:w-1/2 mb-4">
                        <p class="text-sm text-gray-600 mb-1">Current Period</p>
                        <p class="text-sm">{{ formatDate(subscription.current_period_start) }} - {{ formatDate(subscription.current_period_end) }}</p>
                      </div>
                      <div class="w-full md:w-1/2 mb-4">
                        <p class="text-sm text-gray-600 mb-1">Next Billing Date</p>
                        <p class="text-sm">{{ formatDate(subscription.current_period_end) }}</p>
                      </div>
                    </div>
                    
                    <div v-if="subscription.cancel_at_period_end" class="mt-4 p-3 bg-yellow-100 rounded-lg">
                      <p class="text-sm text-yellow-800">
                        <i class="fa fa-exclamation-triangle mr-2"></i>
                        Your subscription will be canceled on {{ formatDate(subscription.current_period_end) }}
                      </p>
                    </div>
                  </div>

                  <!-- No Subscription Message -->
                  <div v-else class="no-subscription-card mb-6 p-6 bg-yellow-50 rounded-lg">
                    <h3 class="text-lg font-semibold mb-2">No Active Subscription</h3>
                    <p class="text-gray-600 mb-4">You don't have an active subscription. Choose a plan to get started.</p>
                  </div>

                  <!-- Available Plans -->
                  <h3 class="text-lg font-semibold mb-4">
                    {{ subscription ? 'Change Plan' : 'Choose a Plan' }}
                  </h3>
                  
                  <div class="plans-grid grid grid-cols-1 md:grid-cols-2 gap-6">
                    <!-- Monthly Plan -->
                    <div class="plan-card border rounded-lg p-6" 
                         :class="{'border-green-500 bg-green-50': subscription && subscription.plan_type === 'monthly'}">
                      <div class="plan-header mb-4">
                        <h4 class="text-xl font-semibold">Monthly Plan</h4>
                        <p class="text-gray-600">Flexible monthly billing</p>
                      </div>
                      <div class="plan-price mb-4">
                        <span class="text-3xl font-bold">$10</span>
                        <span class="text-gray-600">/month</span>
                      </div>
                      <ul class="plan-features mb-6 space-y-2">
                        <li class="flex items-center">
                          <i class="fa fa-check text-green-500 mr-2"></i>
                          <span>All features included</span>
                        </li>
                        <li class="flex items-center">
                          <i class="fa fa-check text-green-500 mr-2"></i>
                          <span>Unlimited grid scans</span>
                        </li>
                        <li class="flex items-center">
                          <i class="fa fa-check text-green-500 mr-2"></i>
                          <span>Cancel anytime</span>
                        </li>
                      </ul>
                      <button 
                        v-if="!subscription || subscription.plan_type !== 'monthly'"
                        @click="changePlan('monthly')"
                        class="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        :disabled="changingPlan">
                        <span v-if="changingPlan">
                          <i class="fa fa-spinner fa-spin mr-2"></i> Processing...
                        </span>
                        <span v-else>
                          {{ subscription ? 'Switch to Monthly' : 'Select Monthly' }}
                        </span>
                      </button>
                      <div v-else class="w-full px-4 py-2 bg-gray-200 text-gray-600 rounded-lg text-center">
                        Current Plan
                      </div>
                    </div>

                    <!-- Yearly Plan -->
                    <div class="plan-card border rounded-lg p-6 relative"
                         :class="{'border-green-500 bg-green-50': subscription && subscription.plan_type === 'yearly'}">
                      <!-- <div class="absolute top-2 right-2 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                        Save $120/year
                      </div> -->
                      <div class="plan-header mb-4">
                        <h4 class="text-xl font-semibold">Yearly Plan</h4>
                        <p class="text-gray-600">Best value with annual billing</p>
                      </div>
                      <div class="plan-price mb-4">
                        <span class="text-3xl font-bold">$120</span>
                        <span class="text-gray-600">/year</span>
                      </div>
                      <ul class="plan-features mb-6 space-y-2">
                        <li class="flex items-center">
                          <i class="fa fa-check text-green-500 mr-2"></i>
                          <span>All features included</span>
                        </li>
                        <li class="flex items-center">
                          <i class="fa fa-check text-green-500 mr-2"></i>
                          <span>Unlimited grid scans</span>
                        </li>
                        <li class="flex items-center">
                          <i class="fa fa-check text-green-500 mr-2"></i>
                          <span>50% discount</span>
                        </li>
                      </ul>
                      <button 
                        v-if="!subscription || subscription.plan_type !== 'yearly'"
                        @click="changePlan('yearly')"
                        class="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        :disabled="changingPlan">
                        <span v-if="changingPlan">
                          <i class="fa fa-spinner fa-spin mr-2"></i> Processing...
                        </span>
                        <span v-else>
                          {{ subscription ? 'Switch to Yearly' : 'Select Yearly' }}
                        </span>
                      </button>
                      <div v-else class="w-full px-4 py-2 bg-gray-200 text-gray-600 rounded-lg text-center">
                        Current Plan
                      </div>
                    </div>
                  </div>

                  <!-- Cancel/Reactivate Buttons -->
                  <div v-if="subscription" class="mt-8 flex flex-wrap gap-4">
                    <button 
                      v-if="!subscription.cancel_at_period_end"
                      @click="cancelSubscription"
                      class="cancel-subscription-btn"
                      :disabled="cancelingSubscription">
                      <span v-if="cancelingSubscription">
                        <i class="fa fa-spinner fa-spin mr-2"></i> Processing...
                      </span>
                      <span v-else>
                        <i class="fa fa-times-circle mr-2"></i>
                        Cancel Subscription
                      </span>
                    </button>
                    <button 
                      v-else
                      @click="reactivateSubscription"
                      class="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      :disabled="reactivatingSubscription">
                      <span v-if="reactivatingSubscription">
                        <i class="fa fa-spinner fa-spin mr-2"></i> Processing...
                      </span>
                      <span v-else>
                        Reactivate Subscription
                      </span>
                    </button>
                  </div>
                </div>
              </v-tab-item>

              <!-- Invoices Tab -->
              <v-tab-item class="pt-5">
                <div class="invoices-section">
                  <h3 class="text-lg font-semibold mb-4">Invoice History</h3>
                  
                  <div v-if="loadingInvoices" class="text-center py-8">
                    <i class="fa fa-spinner fa-spin text-2xl text-gray-500"></i>
                  </div>

                  <div v-else-if="invoices.length === 0" class="text-center py-8 bg-gray-50 rounded-lg">
                    <i class="fa fa-file-invoice text-4xl text-gray-400 mb-2"></i>
                    <p class="text-gray-600">No invoices found</p>
                  </div>

                  <div v-else class="overflow-x-auto">
                    <table class="w-full border-collapse">
                      <thead>
                        <tr class="border-b bg-gray-50">
                          <th class="text-left py-3 px-4 font-semibold text-sm">Invoice #</th>
                          <th class="text-left py-3 px-4 font-semibold text-sm">Date</th>
                          <th class="text-left py-3 px-4 font-semibold text-sm">Amount</th>
                          <th class="text-left py-3 px-4 font-semibold text-sm">Status</th>
                          <th class="text-left py-3 px-4 font-semibold text-sm">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="invoice in invoices" :key="invoice.id" class="border-b hover:bg-gray-50">
                          <td class="py-3 px-4">
                            <span class="font-mono text-sm">{{ invoice.invoice_number }}</span>
                          </td>
                          <td class="py-3 px-4">
                            {{ formatDate(invoice.createdAt) }}
                          </td>
                          <td class="py-3 px-4">
                            ${{ parseFloat(invoice.amount_paid).toFixed(2) }} {{ (invoice.currency || 'usd').toUpperCase() }}
                          </td>
                          <td class="py-3 px-4">
                            <span :class="getInvoiceStatusClass(invoice.status)" 
                                  class="px-2 py-1 rounded-full text-xs font-semibold">
                              {{ invoice.status }}
                            </span>
                          </td>
                          <td class="py-3 px-4">
                            <div class="flex gap-2">
                              <button 
                                @click="viewInvoice(invoice)"
                                class="text-blue-600 hover:text-blue-800 text-sm"
                                title="View Invoice">
                                <i class="fa fa-eye"></i>
                              </button>
                              <button 
                                @click="downloadInvoice(invoice)"
                                class="text-green-600 hover:text-green-800 text-sm"
                                title="Download PDF">
                                <i class="fa fa-download"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Pagination -->
                  <div v-if="totalInvoices > invoicesPerPage" class="mt-4 flex justify-between items-center">
                    <p class="text-sm text-gray-600">
                      Showing {{ (currentPage - 1) * invoicesPerPage + 1 }} to 
                      {{ Math.min(currentPage * invoicesPerPage, totalInvoices) }} of 
                      {{ totalInvoices }} invoices
                    </p>
                    <div class="flex gap-2">
                      <button 
                        @click="previousPage"
                        :disabled="currentPage === 1"
                        class="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed">
                        Previous
                      </button>
                      <button 
                        @click="nextPage"
                        :disabled="currentPage * invoicesPerPage >= totalInvoices"
                        class="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed">
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </v-tab-item>

              <!-- Payment Method Tab -->
              <v-tab-item class="pt-5">
                <div class="payment-method-section">
                  <h3 class="text-lg font-semibold mb-4">Payment Method</h3>
                  
                  <div class="p-6 bg-gray-50 rounded-lg">
                    <p class="text-gray-600 mb-4">
                      Manage your payment methods, update billing information, and view payment history through our secure customer portal.
                    </p>
                    <button 
                      @click="openCustomerPortal"
                      class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                      <i class="fa fa-external-link-alt mr-2"></i>
                      Open Customer Portal
                    </button>
                  </div>

                  <div class="mt-6 p-4 bg-blue-50 rounded-lg">
                    <p class="text-sm text-blue-800">
                      <i class="fa fa-info-circle mr-2"></i>
                      The customer portal allows you to:
                    </p>
                    <ul class="mt-2 ml-6 text-sm text-blue-700 list-disc">
                      <li>Update payment methods</li>
                      <li>Change billing address</li>
                      <li>Download invoices</li>
                      <li>View complete payment history</li>
                    </ul>
                  </div>
                </div>
              </v-tab-item>
            </v-tabs>
          </div>
        </v-card>
      </div>
    </div>
    -->
    <!-- ===== SUBSCRIPTION_FEATURE_END ===== -->

    <!-- Free Access Message -->
    <div class="free-access-message">
      <h1 class="text-lg font-semibold mb-4">Account Status</h1>
      <hr class="border-b border-gray-100 mb-3">

      <div class="dashboard-box">
        <v-card class="pa-4" tile>
          <div class="status-card text-center py-8">
            <i class="fa fa-check-circle text-5xl text-green-500 mb-4"></i>
            <h2 class="text-2xl font-semibold mb-2">Free Access Active</h2>
            <p class="text-gray-600">You have full access to all features.</p>
            <p class="text-sm text-gray-500 mt-4">No subscription required at this time.</p>
          </div>
        </v-card>
      </div>
    </div>

  </panel-navigation>
</template>

<script>
import PanelNavigation from '@/components/PanelNavigation.vue';
import axios from 'axios';

export default {
    metaInfo () {
    return {
      title: "Billing | " + this.$appName
    }
  },
  name: 'Billing',
  components: {
    PanelNavigation
  },
  data() {
    return {
      activeTab: 0,
      loading: true,
      loadingInvoices: false,
      subscription: null,
      invoices: [],
      totalInvoices: 0,
      currentPage: 1,
      invoicesPerPage: 10,
      changingPlan: false,
      cancelingSubscription: false,
      reactivatingSubscription: false
    };
  },
  mounted() {
    this.fetchSubscription();
    this.fetchInvoices();
  },
  methods: {
    async fetchSubscription() {
      try {
        // Ensure we have a valid token
        const token = (this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken']) || localStorage.getItem('auth_token');
        if (!token) {
          this.$router.push('/login');
          return;
        }
        
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/subscriptions/subscription`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.data.hasSubscription) {
          this.subscription = response.data.subscription;
          
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: 'Unable to load subscription details'
        });
      } finally {
        this.loading = false;
      }
    },

    async fetchInvoices() {
      this.loadingInvoices = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/subscriptions/invoices`,
          {
            headers: {
              'Authorization': `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`
            },
            params: {
              limit: this.invoicesPerPage,
              offset: (this.currentPage - 1) * this.invoicesPerPage
            }
          }
        );

        this.invoices = response.data.invoices;
        this.totalInvoices = response.data.total;
      } catch (error) {
        console.error('Error fetching invoices:', error);
      } finally {
        this.loadingInvoices = false;
      }
    },

    async changePlan(planType) {
      // If no subscription, create new one
      if (!this.subscription) {
        // Track subscription initiation
        this.$posthog.capture('subscription_initiated', {
          plan_type: planType,
          action: 'new_subscription'
        });
        
        const result = await this.$swal({
          title: `Subscribe to ${planType === 'monthly' ? 'Monthly' : 'Yearly'} Plan?`,
          text: 'You will be redirected to our secure payment page.',
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#3ebb80',
          cancelButtonColor: '#757B89',
          confirmButtonText: 'Yes, proceed'
        });

        if (!result.isConfirmed) return;

        try {
          this.changingPlan = true;
          // Store session info before redirect
          const token = (this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken']) || localStorage.getItem('auth_token');
          
          const response = await axios.post(
            `${process.env.VUE_APP_API_ENDPOINT}/api/subscriptions/create-checkout-session`,
            { planType },
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );

          if (response.data.url) {
            window.location.href = response.data.url;
          }
        } catch (error) {
          console.error('Error creating checkout session:', error);
          this.$swal({
            icon: 'error',
            title: 'Error',
            text: 'Unable to start checkout process'
          });
        } finally {
          this.changingPlan = false;
        }
        return;
      }

      // Change existing subscription
      const currentPlan = this.subscription.plan_type;
      const newPlan = planType;

      const result = await this.$swal({
        title: 'Change Subscription Plan?',
        html: `You are switching from <b>${currentPlan}</b> to <b>${newPlan}</b> plan.<br><br>
               The change will take effect immediately and you'll be charged/credited prorated amounts.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3ebb80',
        cancelButtonColor: '#757B89',
        confirmButtonText: 'Yes, change plan'
      });

      if (!result.isConfirmed) return;

      try {
        this.changingPlan = true;
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/subscriptions/update-plan`,
          { newPlanType: planType },
          {
            headers: {
              'Authorization': `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`
            }
          }
        );

        if (response.data.success) {
          this.$swal({
            icon: 'success',
            title: 'Success',
            text: 'Your subscription plan has been updated!'
          });
          await this.fetchSubscription();
          await this.$store.dispatch('auth/fetchUserProfile');
        }
      } catch (error) {
        console.error('Error updating plan:', error);
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.error) || 'Unable to update subscription plan'
        });
      } finally {
        this.changingPlan = false;
      }
    },

    async cancelSubscription() {
      const result = await this.$swal({
        title: 'Cancel Subscription?',
        text: 'Your subscription will remain active until the end of the current billing period.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#757B89',
        confirmButtonText: 'Yes, cancel subscription'
      });

      if (!result.isConfirmed) return;

      try {
        this.cancelingSubscription = true;
        
        // Track cancellation attempt
        this.$posthog.capture('subscription_cancelled', {
          plan_type: this.subscription ? this.subscription.plan_type : 'unknown',
          cancel_immediately: false
        });
        
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/subscriptions/cancel`,
          { immediately: false },
          {
            headers: {
              'Authorization': `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`
            }
          }
        );

        if (response.data.success) {
          this.$swal({
            icon: 'success',
            title: 'Subscription Canceled',
            text: 'Your subscription will remain active until the end of the billing period.'
          });
          await this.fetchSubscription();
          await this.$store.dispatch('auth/fetchUserProfile');
        }
      } catch (error) {
        console.error('Error canceling subscription:', error);
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: 'Unable to cancel subscription'
        });
      } finally {
        this.cancelingSubscription = false;
      }
    },

    async reactivateSubscription() {
      const result = await this.$swal({
        title: 'Reactivate Subscription?',
        text: 'Your subscription will continue with the current plan.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3ebb80',
        cancelButtonColor: '#757B89',
        confirmButtonText: 'Yes, reactivate'
      });

      if (!result.isConfirmed) return;

      try {
        this.reactivatingSubscription = true;
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/subscriptions/reactivate`,
          {},
          {
            headers: {
              'Authorization': `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`
            }
          }
        );

        if (response.data.success) {
          this.$swal({
            icon: 'success',
            title: 'Success',
            text: 'Your subscription has been reactivated!'
          });
          await this.fetchSubscription();
          await this.$store.dispatch('auth/fetchUserProfile');
        }
      } catch (error) {
        console.error('Error reactivating subscription:', error);
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: 'Unable to reactivate subscription'
        });
      } finally {
        this.reactivatingSubscription = false;
      }
    },

    async downloadInvoice(invoice) {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/subscriptions/invoice/${invoice.id}/download`,
          {
            headers: {
              'Authorization': `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`
            }
          }
        );

        if (response.data.url) {
          window.open(response.data.url, '_blank');
        }
      } catch (error) {
        console.error('Error downloading invoice:', error);
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: 'Unable to download invoice'
        });
      }
    },

    viewInvoice(invoice) {
      if (invoice.hosted_invoice_url) {
        window.open(invoice.hosted_invoice_url, '_blank');
      } else {
        this.downloadInvoice(invoice);
      }
    },

    async openCustomerPortal() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/subscriptions/portal-url`,
          {
            headers: {
              'Authorization': `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`
            }
          }
        );

        if (response.data.url) {
          window.open(response.data.url, '_blank');
        }
      } catch (error) {
        console.error('Error opening customer portal:', error);
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: 'Unable to open customer portal'
        });
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    getStatusClass(status) {
      const classes = {
        'active': 'bg-green-100 text-green-800',
        'trialing': 'bg-blue-100 text-blue-800',
        'past_due': 'bg-yellow-100 text-yellow-800',
        'canceled': 'bg-red-100 text-red-800',
        'unpaid': 'bg-red-100 text-red-800'
      };
      return classes[status] || 'bg-gray-100 text-gray-800';
    },

    getInvoiceStatusClass(status) {
      const classes = {
        'paid': 'bg-green-100 text-green-800',
        'open': 'bg-yellow-100 text-yellow-800',
        'draft': 'bg-gray-100 text-gray-800',
        'void': 'bg-red-100 text-red-800',
        'uncollectible': 'bg-red-100 text-red-800'
      };
      return classes[status] || 'bg-gray-100 text-gray-800';
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.fetchInvoices();
      }
    },

    nextPage() {
      if (this.currentPage * this.invoicesPerPage < this.totalInvoices) {
        this.currentPage++;
        this.fetchInvoices();
      }
    }
  }
};
</script>

<style scoped>
.dashboard-box {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.plan-card {
  transition: all 0.3s ease;
}

.plan-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.current-plan-card {
  border-left: 4px solid #3ebb80;
}

.no-subscription-card {
  border-left: 4px solid #f59e0b;
}

.cancel-subscription-btn {
  padding: 0.5rem 1.5rem;
  background-color: #fff;
  color: #e11d48;
  border: 1px solid #fda4af;
  border-radius: 0.5rem;
  font-weight: 500;
  transition: all 0.2s;
  cursor: pointer;
}

.cancel-subscription-btn:hover:not(:disabled) {
  background-color: #fef2f2;
  color: #be123c;
  border-color: #fb7185;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(225, 29, 72, 0.1);
}

.cancel-subscription-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>